import { useId, useMemo } from 'react';
import { Text } from '@lmn/design-system';
import {
  getRoomBedTypesOptions,
  MealPlanOption,
  MealPlanOptionGroup,
  MealPlanOptionGroupTitle,
  StyledRadio,
} from './RoomPanelCommon';
import { useTranslation } from '../../translations/Translation';

export function RoomBedTypeSelection(props: {
  context: 'desktop-header' | 'room-panel-modal' | 'room-panel';
  onSelectMealPlan?: (selectedMealPlan: MealPlanType) => void;
  room: RoomType;
  selectedMealPlan: MealPlanType;
}) {
  const { context, selectedMealPlan, room, onSelectMealPlan } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const preferenceLabel = t('vacation.select.room_panel.bedding.preference');
  const bedTypes = useMemo(() => getRoomBedTypesOptions(room.configurations, room.bedOptions), [room.configurations]);
  const bedTypesOptions = Object.values(bedTypes).sort((a, b) => {
    if (a.code < b.code) {
      return -1;
    }
    if (a.code > b.code) {
      return 1;
    }
    return 0;
  });
  const mealPlans = room.configurations;

  const onChangeBedOption = (targetMealPlan: MealPlanType | undefined) => {
    if (targetMealPlan) {
      onSelectMealPlan?.(targetMealPlan);
    }
  };

  return bedTypesOptions?.length ? (
    <>
      <MealPlanOptionGroupTitle
        id={titleId}
        css={`
          align-items: baseline;
          flex-wrap: wrap;
        `}
      >
        <span
          css={`
            margin-right: 4px;
          `}
        >
          {preferenceLabel}
        </span>
        <Text
          variant="Body"
          css={`
            text-transform: none;
          `}
        >
          {t('vacation.select.room_panel.bedding.if_available')}
        </Text>
      </MealPlanOptionGroupTitle>
      <MealPlanOptionGroup role="radiogroup" aria-labelledby={preferenceLabel ? titleId : undefined}>
        {bedTypesOptions.map((bedOption) => {
          const targetMealPlan = mealPlans.find(
            (mealPlan) =>
              selectedMealPlan.boardClass === mealPlan.boardClass &&
              selectedMealPlan.cancellationPolicy.type === mealPlan.cancellationPolicy.type &&
              selectedMealPlan.cancellationPolicy.upTo === mealPlan.cancellationPolicy.upTo &&
              mealPlan.bedId === bedOption.code
          );

          const disabled = targetMealPlan === undefined;
          const checked = selectedMealPlan.bedId === bedOption.code;
          return (
            <MealPlanOption
              key={bedOption.code}
              tabIndex={disabled ? -1 : 0}
              role="radio"
              aria-checked={checked}
              aria-disabled={disabled}
              onClick={() => !disabled && onChangeBedOption(targetMealPlan)}
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  e.stopPropagation();
                  const currentTarget = e.currentTarget as HTMLElement;
                  onChangeBedOption(targetMealPlan);
                  requestAnimationFrame(() => {
                    currentTarget.focus();
                  });
                }
              }}
            >
              <StyledRadio
                label={
                  <Text variant="BodySmall">
                    {bedOption.beds
                      .map((bed) => `${bed.quantity} ${bed.name}`)
                      .join(` ${t('vacation.select.room_panel.bedding.and')} `)}
                  </Text>
                }
                name={`${context}-bed-preference-${room.roomId}-${room.roomsIncludedInPrice}`}
                value={`${bedOption.code}`}
                data-testid={`bed-option-${bedOption.code}`}
                hoverBackground={false}
                readOnly={true}
                checked={checked}
                disabled={disabled}
                tabIndex={-1}
              />
            </MealPlanOption>
          );
        })}
      </MealPlanOptionGroup>
    </>
  ) : undefined;
}

export function shouldShowBedSelection(
  mealPlanBedId: string | undefined,
  bedOptions: StaticRoomBedOption[] | undefined
) {
  return (
    mealPlanBedId &&
    bedOptions &&
    bedOptions.length > 1 &&
    bedOptions.findIndex((option) => option.code === mealPlanBedId) !== -1
  );
}
